import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
// `Upload` (arrow out of a tray), not `Share2` — it's the share glyph the
// design uses, and the one the rest of the mock was drawn around.
import { Calendar, MapPin, Globe, Upload } from "lucide-react";
import { FaInstagram, FaFacebookF, FaXTwitter, FaLinkedinIn, FaTiktok } from "react-icons/fa6";

import organizerServices from "../../services/organizerServices";
import NotFound from "../../components/errors/NotFound";
import SocialShare from "../../components/modal/SocialShare";
import { openModal, showError } from "../../stores/slices/commonSlice";
import { readFollowCookie, writeFollowCookie } from "../../utils/followCookies";

/* =========================================================================
 *  Organizer profile — the public organizer page (/organizer/:identifier).
 *
 *  Brand header (avatar, name, follower count, Follow CTA, share), then a
 *  three-tab body: their upcoming events, their past events, and an
 *  "Organizer Info" panel with stats, bio and social links.
 *
 *  URL shape: /organizer/maragh-ent — the organizer's username slug
 *  (App\Support\HostIdentifier — "host" there names the events.host_id
 *  relationship, not the public wording). A slug retired by a rename still
 *  resolves, as does the older /host/* prefix; routes/web.php answers both
 *  with a real 301, and this page additionally rewrites the address bar to
 *  the canonical path so share links converge on one URL per organizer.
 *
 *  Backed by the public GET /organizer/:identifier endpoint, which returns
 *  everything the page needs in one round-trip. Follow/unfollow reuse the
 *  same /organizer/:slug/follow endpoints (and the same `_aps_fwd_<slug>`
 *  cookie cache) as the organizer card on the event details page, so the two
 *  surfaces never disagree about follow state.
 * ========================================================================= */

const BRAND_YELLOW = "#ffea00";
const TAB_ACTIVE = "#3659e3";

// Social links, in the order the design lays them out. `baseUrl` is prepended
// when the host stored a bare handle; a full URL is used as-is.
const SOCIAL_LINKS = [
    { key: "website", label: "Website", Icon: Globe, baseUrl: "https://" },
    { key: "facebook", label: "Facebook", Icon: FaFacebookF, baseUrl: "https://facebook.com/" },
    { key: "instagram", label: "Instagram", Icon: FaInstagram, baseUrl: "https://instagram.com/" },
    { key: "twitter", label: "X (Twitter)", Icon: FaXTwitter, baseUrl: "https://x.com/" },
    { key: "tiktok", label: "TikTok", Icon: FaTiktok, baseUrl: "https://tiktok.com/@" },
    { key: "linkedin", label: "LinkedIn", Icon: FaLinkedinIn, baseUrl: "https://linkedin.com/in/" },
];

// Build a full profile URL from the stored value. Hosts typically save just
// the handle ("apsession"), so we prepend the platform base URL; a value that
// is already a full URL (legacy data or a pasted link) passes through, and a
// leading "@" on a handle is stripped.
const socialHref = (value, baseUrl) => {
    if (typeof value !== "string") return null;
    const handle = value.trim().replace(/^@+/, "");
    if (handle === "") return null;
    if (/^https?:\/\//i.test(handle)) return handle;
    return `${baseUrl}${handle}`;
};

// Compact counts: 980 → "980", 1_500 → "1.5k", 1_500_000 → "1.5M".
const formatCount = (n) => {
    const v = Number(n) || 0;
    if (v < 1000) return String(v);
    if (v < 1_000_000) return (v / 1000).toFixed(1).replace(/\.0$/, "") + "k";
    if (v < 1_000_000_000) return (v / 1_000_000).toFixed(1).replace(/\.0$/, "") + "M";
    return (v / 1_000_000_000).toFixed(1).replace(/\.0$/, "") + "B";
};

// Parse "YYYY-MM-DD" into a LOCAL date. `new Date("2026-06-18")` is parsed as
// UTC midnight and renders as the 17th in every negative-offset timezone, so
// we build from the parts instead.
const toLocalDate = (value) => {
    if (!value) return null;
    const [y, m, d] = String(value).slice(0, 10).split("-").map(Number);
    if (!y || !m || !d) return null;
    const date = new Date(y, m - 1, d);
    return Number.isNaN(date.getTime()) ? null : date;
};

// "19:00:00" → "7:00 PM". Returns "" for an empty or unparseable value.
const formatTime = (value) => {
    const [h, m] = String(value || "").split(":");
    const hour = Number(h);
    if (!Number.isFinite(hour) || m === undefined) return "";
    const suffix = hour >= 12 ? "PM" : "AM";
    const hour12 = hour % 12 === 0 ? 12 : hour % 12;
    return `${hour12}:${m.padStart(2, "0")} ${suffix}`;
};

// Upcoming: "Fri, Aug 28 • 7:00 PM" — the year is appended only when the event
// falls outside the current year, which keeps the common case short.
// Past: "Sat, Jul 10, 2026" — always year-stamped (a past event's year is the
// thing you actually want to know) and without a start time, which is noise
// once the event is over.
const formatEventDate = (event, isPast) => {
    const date = toLocalDate(event?.date);
    if (!date) return "";

    const showYear = isPast || date.getFullYear() !== new Date().getFullYear();
    const label = date.toLocaleDateString("en-US", {
        weekday: "short",
        month: "short",
        day: "numeric",
        ...(showYear ? { year: "numeric" } : {}),
    });

    if (isPast) return label;

    const time = formatTime(event?.time);
    return time ? `${label} • ${time}` : label;
};

// Venue first, then the broadest place name we have. Deduped so a venue that
// already contains the city isn't printed twice.
const formatLocation = (event) => {
    const place = [event?.city, event?.parish, event?.country].find(Boolean);
    const parts = [event?.venue, place].filter(Boolean);
    const unique = parts.filter(
        (part, i) => !parts.some((other, j) => j < i && other.toLowerCase().includes(part.toLowerCase()))
    );
    return unique.join(", ") || "Location to be announced";
};

const eventPath = (event) => `/events/${event.slug || event.event_link}`;

/* ---------------------------- presentational ---------------------------- */

const VerifiedBadge = () => (
    // Same checkmark the event details host card uses; colour matches X/Twitter blue.
    <svg
        className="w-5 h-5 ml-1.5 flex-shrink-0"
        style={{ color: "#1d9bf0" }}
        fill="currentColor"
        viewBox="0 0 24 24"
        role="img"
        aria-label="Verified"
    >
        <path d="M22.5 12.5c0-1.58-.875-2.95-2.148-3.6.154-.435.238-.905.238-1.4 0-2.21-1.71-3.998-3.918-3.998-.47 0-.92.084-1.336.25C14.818 2.415 13.51 1.5 12 1.5s-2.816.917-3.337 2.25c-.416-.165-.866-.25-1.336-.25-2.21 0-3.918 1.79-3.918 4 0 .495.084.965.238 1.4-1.273.65-2.148 2.02-2.148 3.6 0 1.46.756 2.76 1.88 3.45-.044.17-.07.35-.07.53 0 2.21 1.71 4 3.918 4 .47 0 .92-.086 1.336-.25.52 1.334 1.82 2.25 3.337 2.25s2.816-.916 3.337-2.25c.416.164.866.25 1.336.25 2.21 0 3.918-1.79 3.918-4 0-.18-.026-.36-.07-.53 1.124-.69 1.88-1.99 1.88-3.45zm-12.08 4.29l-3.23-3.23 1.41-1.41 1.82 1.82 5.07-5.07 1.41 1.42-6.48 6.47z" />
    </svg>
);

// `no-underline` overrides Bootstrap's reboot (Bootstrap loads in App.jsx and
// underlines every bare <a>); the title keeps its own group-hover underline.
const HostEventCard = ({ event, isPast }) => (
    <Link
        to={eventPath(event)}
        className="group no-underline bg-white w-full rounded-xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300 flex flex-col border border-gray-200"
    >
        <div className="relative aspect-[16/9] w-full overflow-hidden bg-gray-100 shrink-0">
            <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent group-hover:from-black/50 transition-colors z-10" />
            <img
                src={event.horizontal_flyer || "/assets/images/event-cover-paceholder.png"}
                alt={event.name}
                loading="lazy"
                className={`w-full h-full object-cover transform group-hover:scale-105 transition-transform duration-700 ease-out ${
                    isPast ? "grayscale-[35%] group-hover:grayscale-0" : ""
                }`}
            />
            {event.genre && (
                <div className="absolute bottom-4 right-4 z-20 flex flex-col items-end">
                    <span
                        className="text-black text-[10px] font-black px-2.5 py-1.5 rounded-md uppercase tracking-wider shadow-md border border-yellow-400"
                        style={{ backgroundColor: BRAND_YELLOW }}
                    >
                        {event.genre}
                    </span>
                </div>
            )}
        </div>

        <div className="p-5 sm:p-6 flex flex-col flex-grow">
            <h3 className="text-lg sm:text-xl font-black text-gray-900 mb-4 line-clamp-2 leading-tight group-hover:underline decoration-4 underline-offset-2 transition-all"
                style={{ textDecorationColor: BRAND_YELLOW }}
            >
                {event.name}
            </h3>

            <div className="flex flex-col space-y-3 mt-auto">
                <div className="flex items-center text-gray-800 text-sm font-bold">
                    <div className="bg-yellow-100/60 p-1.5 rounded-lg mr-3 shadow-sm border border-yellow-400/20">
                        <Calendar size={16} className="text-black" />
                    </div>
                    <span className="truncate">{formatEventDate(event, isPast)}</span>
                </div>
                <div className="flex items-start text-gray-800 text-sm font-bold">
                    <div className="bg-gray-100 p-1.5 rounded-lg mr-3 mt-0.5 shadow-sm border border-gray-200">
                        <MapPin size={16} className="text-black" />
                    </div>
                    <span className="line-clamp-2 leading-snug">{formatLocation(event)}</span>
                </div>
            </div>
        </div>
    </Link>
);

const EventGrid = ({ events, isPast, emptyMessage }) => {
    if (!events || events.length === 0) {
        return (
            <div className="py-16 text-center">
                <Calendar size={48} className="mx-auto text-gray-300 mb-4" />
                <h3 className="text-2xl font-black text-gray-900 mb-2">No Events Found</h3>
                <p className="text-gray-500 font-medium">{emptyMessage}</p>
            </div>
        );
    }

    return (
        <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-6 md:gap-8 pb-8">
            {events.map((event) => (
                <HostEventCard key={event.id} event={event} isPast={isPast} />
            ))}
        </div>
    );
};

const OrganizerInfo = ({ host, stats }) => {
    const links = SOCIAL_LINKS.map((link) => ({
        ...link,
        href: socialHref(host?.socials?.[link.key], link.baseUrl),
    })).filter((link) => link.href);

    return (
        <div className="mt-8 pb-12">
            {/* Stats row — divide-x draws the hairlines between cells, so the
                first cell carries no left padding on mobile. */}
            <div className="flex flex-wrap items-center divide-x divide-gray-200 border-y border-gray-100 py-6 mb-8">
                {[
                    { label: "followers", value: stats.followers },
                    { label: "hosting", value: stats.hosting },
                    { label: "total events", value: stats.total_events },
                    { label: "total attendees", value: stats.total_attendees },
                ].map(({ label, value }) => (
                    <div key={label} className="px-4 sm:px-6 first:pl-0 sm:first:pl-6 flex flex-col">
                        <span className="text-gray-500 font-medium text-sm mb-1">{label}</span>
                        <span className="font-black text-xl text-gray-900">{formatCount(value)}</span>
                    </div>
                ))}
            </div>

            <div className="sm:px-6 mb-8 max-w-3xl">
                <p className="text-gray-700 text-lg leading-relaxed font-medium">
                    {host.bio || "This organizer hasn't added a description yet."}
                </p>
            </div>

            {links.length > 0 && (
                <div className="sm:px-6 flex items-center gap-5 flex-wrap">
                    {links.map(({ key, label, href, Icon }) => (
                        <a
                            key={key}
                            href={href}
                            target="_blank"
                            rel="noopener noreferrer"
                            title={label}
                            aria-label={label}
                            className="text-gray-600 hover:text-black transition-colors"
                        >
                            <Icon size={24} />
                        </a>
                    ))}
                </div>
            )}
        </div>
    );
};

const OrganizerProfileSkeleton = () => (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 animate-pulse">
        <div className="flex items-start gap-5">
            <div className="w-20 h-20 sm:w-24 sm:h-24 rounded-full bg-gray-200 flex-shrink-0" />
            <div className="flex-1 pt-1">
                <div className="h-8 w-52 bg-gray-200 rounded-lg mb-4" />
                <div className="flex items-center gap-4">
                    <div className="h-10 w-16 bg-gray-200 rounded-lg" />
                    <div className="h-10 w-32 bg-gray-200 rounded-lg" />
                </div>
            </div>
        </div>
        <div className="flex gap-6 border-b border-gray-200 mt-10 pb-3">
            {[0, 1, 2].map((i) => (
                <div key={i} className="h-5 w-28 bg-gray-200 rounded" />
            ))}
        </div>
        <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-6 md:gap-8">
            {[0, 1, 2, 3].map((i) => (
                <div key={i} className="rounded-xl border border-gray-200 overflow-hidden">
                    <div className="aspect-[16/9] bg-gray-200" />
                    <div className="p-6 space-y-3">
                        <div className="h-5 w-3/4 bg-gray-200 rounded" />
                        <div className="h-4 w-1/2 bg-gray-200 rounded" />
                        <div className="h-4 w-2/3 bg-gray-200 rounded" />
                    </div>
                </div>
            ))}
        </div>
    </div>
);

/* --------------------------------- page --------------------------------- */

export default function OrganizerProfile() {
    const { identifier } = useParams();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const isLoggedIn = useSelector((state) => state.auth.isLoggedIn);

    const [profile, setProfile] = useState(null);
    const [loading, setLoading] = useState(true);
    const [notFound, setNotFound] = useState(false);
    const [activeTab, setActiveTab] = useState("upcoming");
    const [openShare, setOpenShare] = useState(false);

    // Follow state is mirrored locally (rather than read straight off
    // `profile`) so the button can flip optimistically. isFollowPending
    // prevents a double-tap from firing two opposite requests.
    const [isFollowing, setIsFollowing] = useState(false);
    const [followersCount, setFollowersCount] = useState(0);
    const [isFollowPending, setIsFollowPending] = useState(false);

    // The canonical path of whatever is currently loaded. The redirect below
    // changes `identifier`, which would otherwise re-run this effect and
    // refetch the profile we just fetched — this is how we recognise our own
    // redirect and skip it.
    const canonicalRef = useRef(null);

    useEffect(() => {
        if (canonicalRef.current === identifier) return;

        const controller = new AbortController();

        setLoading(true);
        setNotFound(false);
        setActiveTab("upcoming");

        organizerServices
            .getProfile(identifier, controller.signal)
            .then((res) => {
                const data = res?.data;
                if (!data?.host) {
                    setNotFound(true);
                    return;
                }
                setProfile(data);
                // The API is authoritative; the cookie only covers the window
                // before it answers (and the guest case, where the API can't
                // know). OR-ing them means a follow made on another device
                // still shows correctly here.
                setIsFollowing(!!data.following || readFollowCookie(data.host.profile_path));
                setFollowersCount(Number(data.stats?.followers || 0));
                writeFollowCookie(data.host.profile_path, !!data.following);

                // Converge on the canonical URL. We got here via a legacy
                // numeric id or a slug left over from before the host renamed
                // themselves — both resolve, but only one should be shared.
                // (Crawlers get a real 301 from routes/web.php; this is for
                // the in-app case.) `replace` so it doesn't add a history
                // entry the back button has to step through.
                const canonical = data.host.profile_path;
                canonicalRef.current = canonical || identifier;
                if (canonical && canonical !== identifier) {
                    navigate(`/organizer/${canonical}`, { replace: true });
                }
            })
            .catch((err) => {
                if (controller.signal.aborted || err?.code === "ERR_CANCELED") return;
                if (err?.response?.status === 404) {
                    setNotFound(true);
                    return;
                }
                dispatch(showError(err?.response?.data?.message || "Could not load this organizer."));
                setNotFound(true);
            })
            .finally(() => {
                if (!controller.signal.aborted) setLoading(false);
            });

        return () => controller.abort();
    }, [identifier, dispatch, navigate]);

    // Optimistic flip, rolled back on failure. Guests get the login modal
    // instead of a 401 round-trip.
    const handleToggleFollow = useCallback(async () => {
        // The canonical slug, not the URL segment we happened to arrive on —
        // the cookie key must match what the event details host card writes.
        const hostId = profile?.host?.profile_path;
        if (!hostId || isFollowPending) return;

        if (!isLoggedIn) {
            dispatch(openModal());
            return;
        }

        const previousFollowing = isFollowing;
        const previousCount = followersCount;
        const nextFollowing = !previousFollowing;

        setIsFollowing(nextFollowing);
        setFollowersCount((c) => Math.max(0, c + (nextFollowing ? 1 : -1)));
        writeFollowCookie(hostId, nextFollowing);
        setIsFollowPending(true);

        try {
            const res = nextFollowing
                ? await organizerServices.follow(hostId)
                : await organizerServices.unfollow(hostId);

            // Trust the server's numbers over the optimistic guess.
            if (typeof res?.data?.following === "boolean") {
                setIsFollowing(res.data.following);
                writeFollowCookie(hostId, res.data.following);
            }
            if (typeof res?.data?.followers_count === "number") {
                setFollowersCount(res.data.followers_count);
            }
        } catch (err) {
            setIsFollowing(previousFollowing);
            setFollowersCount(previousCount);
            writeFollowCookie(hostId, previousFollowing);
            dispatch(showError(err?.response?.data?.message || "Could not update follow status."));
        } finally {
            setIsFollowPending(false);
        }
    }, [profile, isFollowing, followersCount, isFollowPending, isLoggedIn, dispatch]);

    const tabs = useMemo(() => {
        if (!profile) return [];
        return [
            { key: "upcoming", label: `Upcoming (${profile.upcoming_events.length})` },
            { key: "past", label: `Past (${profile.past_events.length})` },
            { key: "info", label: "Organizer Info" },
        ];
    }, [profile]);

    if (loading) return <OrganizerProfileSkeleton />;
    if (notFound || !profile) return <NotFound />;

    const { host, stats } = profile;
    // Live follower count wins over the snapshot in `stats` so the Organizer
    // Info tab agrees with the header after a follow.
    const liveStats = { ...stats, followers: followersCount };

    return (
        <div className="bg-white min-h-screen">
            {/* ---------------------------- hero ---------------------------- */}
            <div className="bg-white pt-8 pb-6">
                <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex items-start gap-5">
                        <div className="relative flex-shrink-0">
                            <div className="w-20 h-20 sm:w-24 sm:h-24 rounded-full border border-gray-100 bg-[#f4f6f8] flex items-center justify-center overflow-hidden">
                                {host.image ? (
                                    <img src={host.image} alt={host.name} className="w-full h-full object-cover" />
                                ) : (
                                    <span className="text-3xl sm:text-4xl font-black text-gray-900">
                                        {host.name.charAt(0).toUpperCase()}
                                    </span>
                                )}
                            </div>
                        </div>

                        <div className="flex flex-col justify-center pt-1 w-full min-w-0">
                            <h1 className="text-2xl sm:text-3xl font-black text-gray-900 leading-tight mb-3 flex items-center capitalize">
                                <span className="truncate">{host.name}</span>
                                {host.verified && <VerifiedBadge />}
                            </h1>

                            <div className="flex items-center gap-4 flex-wrap mt-2">
                                <div className="flex items-center space-x-4">
                                    <div className="flex flex-col">
                                        <span className="font-black text-xl text-gray-900 leading-none">
                                            {formatCount(followersCount)}
                                        </span>
                                        <span className="text-gray-500 font-medium text-xs uppercase tracking-wider mt-1">
                                            {followersCount === 1 ? "Follower" : "Followers"}
                                        </span>
                                    </div>

                                    {/* Your own profile shows no Follow button — the API
                                        rejects a self-follow with 400 anyway. */}
                                    {!profile.is_self && (
                                        <button
                                            type="button"
                                            onClick={handleToggleFollow}
                                            disabled={isFollowPending}
                                            aria-pressed={isFollowing}
                                            className={`font-black py-2 px-8 rounded-lg transition-colors text-sm shadow-sm disabled:opacity-60 ${
                                                isFollowing
                                                    ? "bg-white border-2 border-black text-black hover:bg-gray-100"
                                                    : "text-black hover:brightness-95"
                                            }`}
                                            style={isFollowing ? undefined : { backgroundColor: BRAND_YELLOW }}
                                        >
                                            {isFollowing ? "Following" : "Follow"}
                                        </button>
                                    )}
                                </div>

                                <button
                                    type="button"
                                    onClick={() => setOpenShare(true)}
                                    aria-label="Share this organizer"
                                    className="p-2 border border-gray-300 hover:bg-gray-50 rounded-full text-gray-900 transition-colors flex-shrink-0 ml-auto sm:ml-0"
                                >
                                    <Upload size={18} />
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* ---------------------------- tabs ---------------------------- */}
            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 mt-4 pb-20">
                <div
                    className="flex space-x-6 w-full border-b border-gray-200 overflow-x-auto"
                    style={{ scrollbarWidth: "none" }}
                    role="tablist"
                >
                    {tabs.map(({ key, label }) => (
                        <button
                            key={key}
                            type="button"
                            role="tab"
                            aria-selected={activeTab === key}
                            onClick={() => setActiveTab(key)}
                            className={`pb-3 text-base font-bold whitespace-nowrap transition-all border-b-[3px] ${
                                activeTab === key ? "" : "border-transparent text-gray-500 hover:text-gray-700"
                            }`}
                            style={activeTab === key ? { borderColor: TAB_ACTIVE, color: TAB_ACTIVE } : undefined}
                        >
                            {label}
                        </button>
                    ))}
                </div>

                <div>
                    {activeTab === "upcoming" && (
                        <EventGrid
                            events={profile.upcoming_events}
                            emptyMessage="This organizer has no upcoming events right now."
                        />
                    )}
                    {activeTab === "past" && (
                        <EventGrid
                            events={profile.past_events}
                            isPast
                            emptyMessage="This organizer hasn't wrapped up any events yet."
                        />
                    )}
                    {activeTab === "info" && <OrganizerInfo host={host} stats={liveStats} />}
                </div>
            </div>

            {/* No eventLink passed — SocialShare falls back to the current URL,
                which is exactly this profile page. */}
            <SocialShare open={openShare} handleClose={() => setOpenShare(false)} />
        </div>
    );
}
